import { downloadProviderFile } from "../../../Services/ProviderService";
import React, { useMemo, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
    MdAutoAwesome,
    MdHourglassTop,
    MdArrowForward,
    MdArrowDownward,
    MdInfoOutline,
    MdCheckCircle,
    MdCloudUpload,
    MdEditNote,
    MdFactCheck,
    MdMemory,
    MdSave,
} from "react-icons/md";
import { useAuth } from "../../../Context/AuthContext";
import { APPRoutes } from "../../../Utils/Route";
import { isSuperAdminUser } from "../../../Utils/Permissions";
import { getAegisAiDocumentById, saveAegisAiDocument } from "../../../Services/AegisAiDocumentsService";
import { getAllClientNames } from "../../../Services/ClientService";
import SearchableDropdown from "../../../Components/Subscription/SearchableDropdown";
import DotLottie from "../../../Components/Common/DotLottie";
import { AiFormSection, AiStatusPill, getAiSourceType, inferAiSourceType } from "../AegisAiUi";
import AiSourcePicker from "./AiSourcePicker";
import AiExtractionPreviewModal from "./AiExtractionPreviewModal";
import AiExtractedContentEditor from "./AiExtractedContentEditor";
import useSourceExtraction from "./useSourceExtraction";
import useDocumentHtml from "./useDocumentHtml";

const STATUS_BADGE_STYLE = {
    active: { backgroundColor: "var(--hc-status-success-bg)", color: "var(--hc-status-success-text)", label: "Active", tone: "success" },
    pending: { backgroundColor: "var(--hc-status-pending-bg)", color: "var(--hc-status-pending-text)", label: "Pending", tone: "pending" },
    inactive: { backgroundColor: "var(--hc-status-inactive-bg)", color: "var(--hc-status-inactive-text)", label: "Inactive", tone: "inactive" },
};

const getStatusBadgeMeta = (statusValue) => {
    const normalized = (statusValue || "").toLowerCase().trim();
    if (STATUS_BADGE_STYLE[normalized]) {
        return STATUS_BADGE_STYLE[normalized];
    }
    return {
        backgroundColor: "var(--hc-status-pending-bg)",
        color: "var(--hc-status-pending-text)",
        label: statusValue || "Pending",
        tone: "pending",
    };
};


const INDEXING_STEPS = [
    { icon: MdCloudUpload, title: "Upload & review", text: "Add a document, audio or video file, custom text, or a YouTube / website link, then review the extracted content and confirm." },
    { icon: MdMemory, title: "Process & index", text: "The document is fed into the RAG knowledge pipeline in the background." },
    { icon: MdCheckCircle, title: "Active", text: "Status changes from Pending to Active once vector indexing is complete." },
];

const emptyForm = {
    id: null,
    clientId: null,
    title: "",
    description: "",
    sourceType: "document",
    file: null,
    fileInfo: null,
    existingFileName: "",
    url: "",
    customText: "",
    // Websites: crawl the site's most useful pages instead of only the entered one.
    wholeSite: false,
};

const validationSchema = Yup.object({
    title: Yup.string().trim().required("Title is required"),
    clientId: Yup.string().nullable().required("Client is required"),
    file: Yup.mixed().nullable().test("file-required", "Please upload a file", function (value) {
        const { sourceType, existingFileName } = this.parent;
        if (getAiSourceType(sourceType).kind !== "file") return true;
        return Boolean(value || existingFileName);
    }),
    url: Yup.string().test("url-valid", function (value) {
        const type = getAiSourceType(this.parent.sourceType);
        if (type.kind !== "url") return true;
        const trimmed = String(value || "").trim();
        if (!trimmed) return this.createError({ message: `${type.label} URL is required` });
        return type.pattern.test(trimmed) || this.createError({ message: type.invalidMessage });
    }),
    customText: Yup.string().test("text-required", "Please enter custom text", function (value) {
        if (this.parent.sourceType !== "custom_text") return true;
        return Boolean(String(value || "").trim());
    }),
});

export default function AegisAiDocumentsForm() {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = !!id;

    const { token, decodedToken } = useAuth();
    const isSuperAdmin = isSuperAdminUser(decodedToken);
    const accountId = decodedToken?.account_id;

    const [loading, setLoading] = useState(isEdit);
    const [clients, setClients] = useState([]);
    const [clientsLoading, setClientsLoading] = useState(false);
    const [documentStatus, setDocumentStatus] = useState("Pending");
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [savedDocTitle, setSavedDocTitle] = useState("");
    // What the record held when loaded, so switching source types and back restores it.
    const [initialSource, setInitialSource] = useState({ fileName: "", url: "", wholeSite: false });
    const [showReview, setShowReview] = useState(false);
    const [confirmSaving, setConfirmSaving] = useState(false);
    // The link and text once the user stops typing; extraction starts from this, not from every keystroke.
    const [settledUrl, setSettledUrl] = useState("");
    const [settledCustomText, setSettledCustomText] = useState("");
    // Recording already stored with an audio/video document: { documentId, hasThumbnail }.
    const [savedMedia, setSavedMedia] = useState(null);
    const [compressing, setCompressing] = useState(false);
    // The saved PDF/DOCX is opened in the editor (edit mode, no new file picked).
    const [editingSavedDocument, setEditingSavedDocument] = useState(false);
    const [enableOcr, setEnableOcr] = useState(false);
    // Extracted content loaded from DB for non-document types (audio, video, youtube, website).
    const [extractedText, setExtractedText] = useState("");
    const [initialExtractedText, setInitialExtractedText] = useState("");

    // A new file or link must be extracted and confirmed before it is saved; edits that keep the
    // current source (e.g. a title change) save directly.
    const needsExtraction = (values) => {
        if (!isEdit && values.sourceType === "custom_text") return Boolean(values.customText && values.customText.trim());
        if (getAiSourceType(values.sourceType).kind === "file") return Boolean(values.file);
        const url = values.url.trim();
        const scopeChanged = values.sourceType === "website" && values.wholeSite !== initialSource.wholeSite;
        return Boolean(url) && (url !== initialSource.url || scopeChanged);
    };

    function mapApiToForm(documentData) {
        return {
            id: documentData?.Id ?? null,
            clientId: isSuperAdmin ? documentData?.ClientId : accountId,
            title: documentData?.Title ?? "",
            description: documentData?.Description ?? "",
            sourceType: inferAiSourceType({
                sourceType: documentData?.SourceType,
                url: documentData?.SourceUrl,
                fileName: documentData?.FileName,
            }),
            file: null,
            fileInfo: null,
            // Audio/video documents show the stored recording, not the transcript file behind it.
            existingFileName: documentData?.MediaFileName ?? documentData?.FileName ?? "",
            url: documentData?.SourceUrl ?? "",
            customText: documentData?.TextContent || "",
            wholeSite: Boolean(documentData?.WholeSite),
        };
    }

    const formik = useFormik({
        initialValues: useMemo(() => ({ ...emptyForm, clientId: isSuperAdmin ? null : accountId }), [isSuperAdmin, accountId]),
        enableReinitialize: false,
        validationSchema,
        onSubmit: async (values) => {
            if (needsExtraction(values)) {
                if (!showReview) {
                    setSettledUrl(values.url);
                    setSettledCustomText(values.customText);
                    setShowReview(true);
                    return;
                }
                await handleConfirmExtraction(extraction.result?.extraction_id, { editedText: values.sourceType === 'custom_text' ? values.customText : undefined });
                return;
            }
            const isCustomText = values.sourceType === 'custom_text';
            const isExtractedOrCustom = ['audio', 'video', 'youtube', 'website', 'custom_text', 'document'].includes(values.sourceType);
            const activeText = isCustomText ? (values.customText || extractedText) : extractedText;
            const textChanged = isExtractedOrCustom && activeText !== initialExtractedText;
            const textToSave = textChanged ? activeText : undefined;
            await saveDocument(values, null, { editedText: textToSave });
        },
    });

    // Documents upload the original file, or (when edited in the popup) the edited HTML, which the API
    // saves as a PDF in its place; every other source saves the confirmed extracted text through its extraction id.
    // Audio/video aren't uploaded again: the API keeps the recording sent for extraction and compresses it in the background.
    async function saveDocument(values, extractionId, { editedText, useRawText = false, excludedPages = [] } = {}) {
        try {
            const sourceKind = getAiSourceType(values.sourceType).kind;
            const isDocument = values.sourceType === "document";
            const originalFullName = String(values.file?.name || values.existingFileName || "document.pdf").split(/[\\/]/).pop();
            const rawExt = originalFullName.includes('.') ? originalFullName.substring(originalFullName.lastIndexOf('.')).toLowerCase() : '.pdf';
            const originalExt = rawExt === '.docx' ? '.docx' : '.pdf';
            const documentStem = originalFullName.replace(/\.[^.]+$/, "") || "document";
            const editedDocument = isDocument && editedText ? new File([editedText], `${documentStem}${originalExt}`, { type: "text/html" }) : null;
            const payload = {
                Id: values.id || null,
                ClientId: isSuperAdmin ? values.clientId : accountId,
                Title: values.title,
                Description: values.description || null,
                Type: values.id ? "update" : "create",
                file: isDocument && !editedDocument ? values.file : null,
                edited_document: editedDocument,
                ExtractionId: !isDocument ? extractionId : null,
                EditedText: !isDocument ? editedText || null : null,
                IsOcr: Boolean(enableOcr),
                // Saves the original transcript instead of its cleaned version.
                UseRawText: useRawText,
                // Whole-site extractions: pages unticked in the review.
                ExcludedPages: excludedPages.length ? excludedPages : null,
                ...(values.sourceType !== "document" && {
                    SourceType: values.sourceType,
                    SourceUrl: sourceKind === "url" ? values.url.trim() : null,
                }),
            };
            const response = await saveAegisAiDocument(token, payload);

            if (response?.is_success) {
                setSavedDocTitle(values.title);
                setDocumentStatus("Pending");
                setShowSuccessModal(true);
                return true;
            }
            if (response?.is_success === false && response?.status_code === 500) {
                toast.error("Internal Server Error. Please Contact Support Team.");
            } else {
                toast.error(response?.message || "Failed to save document");
            }
        } catch (error) {
            console.error(error);
            toast.error(error.message || "Failed to save document");
        }
        return false;
    }

    const handleConfirmExtraction = async (extractionId, { editedText, useRawText = false, excludedPages = [] } = {}) => {
        if (editedText && formik.values.sourceType === 'custom_text') {
            formik.setFieldValue('customText', editedText);
        }
        setConfirmSaving(true);
        const saved = await saveDocument(formik.values, extractionId, { editedText, useRawText, excludedPages });
        setConfirmSaving(false);
        if (saved) closeReview();
    };

    const closeReview = () => {
        setShowReview(false);
        setEditingSavedDocument(false);
    };

    React.useEffect(() => {
        const timer = setTimeout(() => setSettledUrl(formik.values.url), 400);
        return () => clearTimeout(timer);
    }, [formik.values.url]);

    React.useEffect(() => {
        const timer = setTimeout(() => setSettledCustomText(formik.values.customText), 350);
        return () => clearTimeout(timer);
    }, [formik.values.customText]);

    // Extraction starts as soon as a new file is picked or a valid new link is pasted, so the review
    // popup usually opens with the text already there.
    const { sourceType: selectedSourceType, file: selectedFile, url: enteredUrl, customText: enteredCustomText, wholeSite } = formik.values;
    const extractionSource = useMemo(() => {
        const type = getAiSourceType(selectedSourceType);
        if (type.key === "custom_text") {
            const trimmed = (settledCustomText || enteredCustomText || "").trim();
            return trimmed ? { sourceType: "custom_text", text: trimmed, file: null, url: null } : null;
        }
        // Documents open as editable HTML instead (documentSource below).
        if (type.key === "document") return null;
        if (type.kind === "file") return selectedFile ? { sourceType: type.key, file: selectedFile, url: null } : null;
        const trimmed = enteredUrl.trim();
        const isWebsite = type.key === "website";
        const unchanged = trimmed === initialSource.url && (!isWebsite || wholeSite === initialSource.wholeSite);
        if (settledUrl !== enteredUrl || !trimmed || unchanged || !type.pattern.test(trimmed)) return null;
        return { sourceType: type.key, url: trimmed, file: null, wholeSite: isWebsite && wholeSite };
    }, [selectedSourceType, selectedFile, enteredUrl, settledUrl, initialSource.url, initialSource.wholeSite, wholeSite, enteredCustomText, settledCustomText]);
    const sourceExtraction = useSourceExtraction(token, extractionSource);
    // A picked PDF/DOCX is converted as soon as it is chosen; a saved one only when "Edit document" is clicked.
    const documentSource = useMemo(() => {
        if (selectedSourceType !== "document") return null;
        if (selectedFile) return { file: selectedFile, enableOcr };
        return editingSavedDocument && id ? { documentId: id, enableOcr } : null;
    }, [selectedSourceType, selectedFile, editingSavedDocument, id, enableOcr]);
    const documentHtml = useDocumentHtml(token, documentSource);
    const extraction = selectedSourceType === "document" ? documentHtml : sourceExtraction;

    const loadDocument = async () => {
        if (!isEdit) return;
        try {
            setLoading(true);
            const response = await getAegisAiDocumentById(id, token);
            if (response?.is_success) {
                const docData = response?.data;
                formik.setValues(mapApiToForm(docData));
                setInitialSource({
                    fileName: docData?.MediaFileName ?? docData?.FileName ?? "",
                    url: docData?.SourceUrl ?? "",
                    wholeSite: Boolean(docData?.WholeSite),
                });
                setEnableOcr(Boolean(docData?.IsOcr));
                // While a just-saved recording is still compressing in the background it can't be played yet.
                setSavedMedia(docData?.MediaFileId || docData?.MediaProcessing
                    ? { documentId: docData.Id, hasThumbnail: Boolean(docData.ThumbnailFileId), processing: Boolean(docData.MediaProcessing) }
                    : null);
                setDocumentStatus(docData?.Status || docData?.IndexingStatus || "Pending");

                const loadedDbText = docData?.ExtractedData || docData?.TextContent || "";
                setExtractedText(loadedDbText);
                setInitialExtractedText(loadedDbText);

                // Ensure text editor has the file content when editing an existing text document
                if (loadedDbText) {
                    formik.setFieldValue("customText", loadedDbText);
                } else if (docData?.FileId) {
                    const fname = (docData?.FileName || '').toLowerCase();
                    if (fname.endsWith('.txt')) {
                        downloadProviderFile(token, docData.FileId)
                            .then((fileRes) => (fileRes?.blob ? fileRes.blob.text() : ''))
                            .then((t) => {
                                if (t) {
                                    formik.setFieldValue('customText', t);
                                    setExtractedText(t);
                                    setInitialExtractedText(t);
                                }
                            })
                            .catch((err) => console.error('Error fetching text file content for edit:', err));
                    }
                }
            } else if (response?.is_success === false && response?.status_code === 500) {
                toast.error("Internal Server Error. Please Contact Support Team.");
            } else {
                toast.error(response?.message || "Failed to load document");
            }
        } catch (error) {
            console.error(error);
            toast.error(error.message || "Failed to load document");
        } finally {
            setLoading(false);
        }
    };

    const loadClients = async () => {
        if (!isSuperAdmin) return;
        try {
            setClientsLoading(true);
            const response = await getAllClientNames(token);
            if (response?.is_success) {
                setClients(Array.isArray(response?.data) ? response.data : []);
            } else {
                setClients([]);
            }
        } catch (error) {
            console.error(error);
            setClients([]);
        } finally {
            setClientsLoading(false);
        }
    };

    React.useEffect(() => {
        if (!token) return;
        loadClients();
        loadDocument();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, isEdit, token, isSuperAdmin]);

    const renderAsterisk = () => <span className="text-danger">*</span>;

    const handleModalClose = () => {
        setShowSuccessModal(false);
        navigate(APPRoutes.AEGIS_AI_DOCUMENTS);
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center min-vh-100">
                <DotLottie />
            </div>
        );
    }

    const currentStatusMeta = getStatusBadgeMeta(documentStatus);

    return (
        <div className="container-fluid p-0 hc-ai-page hc-ai-form">
            <form onSubmit={formik.handleSubmit} className="hc-ai-form-shell bg-white">
                <div className="hc-ai-form-head d-flex flex-wrap align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-3" style={{ minWidth: 0 }}>
                        <button
                            type="button"
                            className="btn btn-light border rounded-circle shadow-sm flex-shrink-0"
                            onClick={() => navigate(-1)}
                            style={{ width: 40, height: 40 }}
                            aria-label="Go back"
                        >
                            &larr;
                        </button>
                        <div style={{ minWidth: 0 }}>
                            <div className="d-flex flex-wrap align-items-center gap-2">
                                <h3 className="fw-bold mb-0 hc-text-primary">{isEdit ? "Edit Document" : "Upload Document"}</h3>
                                {isEdit && <AiStatusPill tone={currentStatusMeta.tone} label={currentStatusMeta.label} />}
                            </div>
                            <small className="text-muted">Documents uploaded here are mapped to the selected client's RAG</small>
                        </div>
                    </div>
                </div>

                <div className="hc-ai-form-body d-grid align-items-stretch">
                    <aside className="hc-ai-form-aside d-flex flex-column">
                        <div className="hc-ai-form-section-head d-flex align-items-center mb-3">
                            <span className="hc-ai-card-head-icon d-inline-flex align-items-center justify-content-center flex-shrink-0">
                                <MdMemory size={19} />
                            </span>
                            <div style={{ minWidth: 0 }}>
                                <h6 className="hc-ai-card-title m-0">How indexing works</h6>
                                <p className="hc-ai-card-sub">What happens after you save</p>
                            </div>
                        </div>
                        <ol className="hc-ai-vsteps m-0 p-0 d-flex flex-column flex-grow-1">
                            {INDEXING_STEPS.map(({ icon: StepIcon, title, text }, index) => (
                                <React.Fragment key={title}>
                                    <li className="hc-ai-vstep d-flex align-items-start bg-white">
                                        <span className={`hc-ai-avatar rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0 text-white hc-ai-avatar-lg ${index === INDEXING_STEPS.length - 1 ? "hc-ai-avatar-gold" : ""}`}>
                                            <StepIcon size={18} />
                                        </span>
                                        <div style={{ minWidth: 0 }}>
                                            <div className="hc-ai-tl-kicker text-uppercase">Step {index + 1}</div>
                                            <div className="hc-ai-step-title">{title}</div>
                                            <div className="hc-ai-step-text">{text}</div>
                                        </div>
                                    </li>
                                    {index < INDEXING_STEPS.length - 1 ? (
                                        <li className="hc-ai-vstep-arrow d-flex justify-content-center align-items-center flex-grow-1 text-white" aria-hidden="true">
                                            <MdArrowDownward size={16} />
                                        </li>
                                    ) : null}
                                </React.Fragment>
                            ))}
                        </ol>
                    </aside>

                    <div className="hc-ai-form-main">
                        <AiFormSection icon={MdEditNote} title="Document Details" subtitle="Give the document a clear title so it is easy to find later">
                            <div className="row g-3">
                                <div className={isSuperAdmin ? "col-md-6" : "col-12"}>
                                    <label className="form-label fw-semibold">Title {renderAsterisk()}</label>
                                    <input
                                        className={`form-control ${formik.touched.title && formik.errors.title ? "is-invalid" : ""}`}
                                        name="title"
                                        placeholder="e.g. Employee Handbook 2026"
                                        {...formik.getFieldProps("title")}
                                    />
                                    <div className="invalid-feedback">{formik.errors.title}</div>
                                </div>

                                {isSuperAdmin ? (
                                    <div className="col-md-6">
                                        <SearchableDropdown
                                            label="Client"
                                            placeholder="Select client"
                                            options={clients.map((client) => ({ value: client.Id, label: client.OrganizationName }))}
                                            value={formik.values.clientId ?? ""}
                                            onChange={(value) => formik.setFieldValue("clientId", value || null)}
                                            disabled={clientsLoading}
                                            loading={clientsLoading}
                                            required
                                            error={formik.touched.clientId && formik.errors.clientId ? formik.errors.clientId : ''}
                                        />
                                    </div>
                                ) : null}

                                <div className="col-12">
                                    <label className="form-label fw-semibold">Description</label>
                                    <textarea
                                        className="form-control"
                                        name="description"
                                        rows={3}
                                        placeholder="What does this document cover?"
                                        {...formik.getFieldProps("description")}
                                    />
                                </div>
                            </div>
                        </AiFormSection>

                        <AiFormSection icon={MdCloudUpload} title="Knowledge Source" subtitle="Choose what to add, then upload the file or paste the link">
                            <AiSourcePicker
                                sourceType={formik.values.sourceType}
                                renderBeforeFile={
                                    formik.values.sourceType === "document"
                                        ? () => (
                                            <div
                                                className={`mb-3 p-3 rounded-3 d-flex align-items-center justify-content-between transition-all shadow-sm ${
                                                    enableOcr ? "ocr-active-card" : "ocr-inactive-card"
                                                }`}
                                                style={{
                                                    cursor: "pointer",
                                                    border: enableOcr ? "1.5px solid #0284c7" : "1.5px solid #cbd5e1",
                                                    borderLeft: enableOcr ? "5px solid #0284c7" : "5px solid #94a3b8",
                                                    background: enableOcr
                                                        ? "linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)"
                                                        : "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                                                    boxShadow: enableOcr ? "0 4px 14px -2px rgba(2, 132, 199, 0.2)" : "0 1px 3px rgba(0,0,0,0.05)",
                                                    transition: "all 0.25s ease-in-out",
                                                }}
                                                onClick={() => setEnableOcr(!enableOcr)}
                                            >
                                                <div className="d-flex align-items-start gap-3 pe-3">
                                                    <div
                                                        className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
                                                        style={{
                                                            width: "40px",
                                                            height: "40px",
                                                            background: enableOcr ? "linear-gradient(135deg, #0284c7 0%, #0369a1 100%)" : "#e2e8f0",
                                                            color: enableOcr ? "#ffffff" : "#64748b",
                                                            boxShadow: enableOcr ? "0 2px 8px rgba(2, 132, 199, 0.35)" : "none",
                                                            transition: "all 0.25s ease-in-out",
                                                        }}
                                                    >
                                                        <MdAutoAwesome size={22} />
                                                    </div>
                                                    <div>
                                                        <div className="d-flex align-items-center flex-wrap gap-2">
                                                            <span className="fw-bold text-dark" style={{ fontSize: "14.5px" }}>
                                                                Enable Deep OCR (Vision AI)
                                                            </span>
                                                            <span
                                                                className="badge text-white"
                                                                style={{
                                                                    fontSize: "10.5px",
                                                                    padding: "2.5px 7px",
                                                                    borderRadius: "5px",
                                                                    background: "linear-gradient(135deg, #0284c7 0%, #2563eb 100%)",
                                                                }}
                                                            >
                                                                GPT-5.6 Luna
                                                            </span>
                                                            {enableOcr ? (
                                                                <span
                                                                    className="badge bg-success text-white border border-success d-inline-flex align-items-center gap-1"
                                                                    style={{ fontSize: "10.5px", padding: "2.5px 8px", borderRadius: "12px" }}
                                                                >
                                                                    <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#fff", display: "inline-block" }}></span>
                                                                    Vision AI Active
                                                                </span>
                                                            ) : (
                                                                <span
                                                                    className="badge bg-light text-secondary border"
                                                                    style={{ fontSize: "10.5px", padding: "2.5px 7px", borderRadius: "5px" }}
                                                                >
                                                                    Recommended for Scanned PDFs & Tables
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="small text-muted mt-1" style={{ fontSize: "12.5px", lineHeight: "1.5" }}>
                                                            Transcribes visual documents, scans, charts, and complex tables using Vision AI before digital extraction. Click anywhere on this card to enable.
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="d-flex align-items-center gap-2 flex-shrink-0 ms-2" onClick={(e) => e.stopPropagation()}>
                                                    <span
                                                        className="fw-bold"
                                                        style={{
                                                            fontSize: "11px",
                                                            letterSpacing: "0.5px",
                                                            color: enableOcr ? "#0284c7" : "#94a3b8",
                                                            userSelect: "none",
                                                        }}
                                                    >
                                                        {enableOcr ? "ON" : "OFF"}
                                                    </span>
                                                    <div className="form-check form-switch mb-0 fs-4">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            role="switch"
                                                            id="enableOcrSwitch"
                                                            checked={enableOcr}
                                                            onChange={(e) => setEnableOcr(e.target.checked)}
                                                            disabled={formik.isSubmitting}
                                                            style={{ cursor: "pointer" }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                        : null
                                }
                                onSourceTypeChange={(next) => {
                                    if (next === formik.values.sourceType) return;
                                    const nextKind = getAiSourceType(next).kind;
                                    formik.setValues({
                                        ...formik.values,
                                        sourceType: next,
                                        file: null,
                                        fileInfo: null,
                                        // A stored file only carries over when it still matches the chosen type.
                                        existingFileName: inferAiSourceType({ fileName: initialSource.fileName }) === next ? initialSource.fileName : "",
                                        url: nextKind === "url" && inferAiSourceType({ url: initialSource.url }) === next ? initialSource.url : "",
                                        wholeSite: next === "website" && initialSource.wholeSite,
                                    }, false);
                                    formik.setFieldTouched("file", false, false);
                                    formik.setFieldTouched("url", false, false);
                                    formik.setFieldTouched("customText", false, false);
                                }}
                                file={formik.values.file}
                                fileInfo={formik.values.fileInfo}
                                existingFileName={formik.values.existingFileName}
                                existingMedia={savedMedia}
                                onFileChange={(file, info) => {
                                    formik.setValues({ ...formik.values, file, fileInfo: info || null }, true);
                                    // Clearing the old pick while a replacement compresses isn't a validation event.
                                    if (file) {
                                        formik.setFieldTouched("file", true, false);
                                        if (formik.values.sourceType === "document") {
                                            setShowReview(true);
                                        }
                                    }
                                }}
                                onCompressingChange={setCompressing}
                                onRemoveExisting={() => {
                                    formik.setFieldValue("existingFileName", "");
                                    formik.setFieldTouched("file", true, false);
                                }}
                                url={formik.values.url}
                                onUrlChange={(value) => formik.setFieldValue("url", value)}
                                wholeSite={formik.values.wholeSite}
                                onWholeSiteChange={(value) => formik.setFieldValue("wholeSite", value)}
                                onUrlBlur={() => formik.setFieldTouched("url", true)}
                                text={formik.values.customText}
                                onTextChange={(value) => formik.setFieldValue("customText", value)}
                                error={
                                    getAiSourceType(formik.values.sourceType).kind === "file"
                                        ? (formik.touched.file || formik.submitCount > 0) && formik.errors.file
                                        : formik.values.sourceType === "custom_text"
                                            ? (formik.touched.customText || formik.submitCount > 0) && formik.errors.customText
                                            : (formik.touched.url || formik.submitCount > 0) && formik.errors.url
                                }
                                disabled={formik.isSubmitting}
                                isEdit={isEdit}
                            />

                            {isEdit && formik.values.sourceType === "document" && !formik.values.file && (formik.values.existingFileName || initialSource.fileName) ? (
                                <button
                                    type="button"
                                    className="hc-ai-btn overflow-hidden d-inline-flex align-items-center justify-content-center gap-2 fw-bold text-nowrap hc-ai-btn-outline mt-3"
                                    onClick={() => {
                                        setEditingSavedDocument(true);
                                        setShowReview(true);
                                    }}
                                    disabled={formik.isSubmitting}
                                >
                                    <MdEditNote size={18} />
                                    Edit document
                                </button>
                            ) : null}
                            {isEdit && ['audio', 'video', 'youtube', 'website', 'custom_text'].includes(formik.values.sourceType) && !formik.values.file && (initialSource.fileName || initialSource.url || extractedText || formik.values.customText) ? (
                                <AiExtractedContentEditor
                                    sourceType={formik.values.sourceType}
                                    text={formik.values.sourceType === 'custom_text' ? (formik.values.customText || extractedText) : extractedText}
                                    initialText={initialExtractedText}
                                    isOcr={Boolean(enableOcr)}
                                    onTextChange={(newText) => {
                                        setExtractedText(newText);
                                        if (formik.values.sourceType === 'custom_text') {
                                            formik.setFieldValue('customText', newText);
                                        }
                                    }}
                                    onReset={() => {
                                        setExtractedText(initialExtractedText);
                                        if (formik.values.sourceType === 'custom_text') {
                                            formik.setFieldValue('customText', initialExtractedText);
                                        }
                                    }}
                                    disabled={formik.isSubmitting}
                                />
                            ) : null}
                            <div className="hc-ai-field-hint d-flex align-items-start gap-2">
                                <MdInfoOutline size={16} />
                                <span>
                                    The content is extracted and shown to you for review first; it is indexed for RAG answering only after you confirm. Status will update from Pending to Active once vector indexing is complete.
                                </span>
                            </div>
                        </AiFormSection>

                        {/* On-Page Clean & Neat Preview Section */}
                        {showReview && (extractionSource || documentSource) ? (
                            <AiExtractionPreviewModal
                                source={extractionSource || {
                                    sourceType: "document", file: selectedFile, url: null,
                                    label: String(formik.values.existingFileName).split(/[\\/]/).pop(),
                                }}
                                title={isEdit ? "Edit document" : undefined}
                                isEdit={Boolean(isEdit)}
                                isOcr={Boolean(enableOcr)}
                                extraction={extraction}
                                saving={confirmSaving}
                                onCancel={closeReview}
                                onConfirm={handleConfirmExtraction}
                                onRetry={extraction.retry}
                            />
                        ) : null}
                    </div>
                </div>

                <div className="hc-ai-form-foot d-flex flex-wrap justify-content-end">
                    <button type="button" className="hc-ai-btn overflow-hidden d-inline-flex align-items-center justify-content-center gap-2 fw-bold text-nowrap hc-ai-btn-outline" onClick={() => navigate(-1)} disabled={formik.isSubmitting}>
                        Cancel
                    </button>
                    <button type="submit" className="hc-ai-btn overflow-hidden d-inline-flex align-items-center justify-content-center gap-2 fw-bold text-nowrap hc-ai-btn-primary px-4" disabled={formik.isSubmitting || compressing}>
                        {compressing ? (
                            <>
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                Compressing...
                            </>
                        ) : formik.isSubmitting ? (
                            <>
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                Saving...
                            </>
                        ) : !isEdit || needsExtraction(formik.values) ? (
                            showReview ? (
                                <>
                                    <MdCheckCircle size={18} />
                                    Confirm &amp; Save
                                </>
                            ) : (
                                <>
                                    <MdFactCheck size={18} />
                                    {formik.values.sourceType === "document" ? "Review & Edit" : <>Extract &amp; Review</>}
                                </>
                            )
                        ) : (
                            <>
                                <MdSave size={18} />
                                Save
                            </>
                        )}
                    </button>
                </div>
            </form>



            {/* Post-Save RAG Ingestion Notice Modal */}
            {showSuccessModal && (
                <div className="hc-ai-modal-backdrop d-flex align-items-center justify-content-center p-3" role="presentation">
                    <div className="hc-ai-modal w-100 overflow-y-auto bg-white" role="dialog" aria-modal="true" aria-label="Document Processing Notice">
                        <div className="hc-ai-modal-hero position-relative overflow-hidden text-center text-white">
                            <span aria-hidden="true" className="hc-ai-orb rounded-circle hc-ai-orb-a" />
                            <span aria-hidden="true" className="hc-ai-orb rounded-circle hc-ai-orb-b" />
                            <div className="hc-ai-modal-icon d-flex align-items-center justify-content-center text-white">
                                <MdHourglassTop size={32} />
                            </div>
                            <h4 className="fw-bold text-white mb-1">Document Processing Started</h4>
                            <p className="small mb-0" style={{ color: "rgba(255, 255, 255, 0.82)" }}>
                                Ingestion &amp; RAG Vector Indexing in progress
                            </p>
                        </div>

                        <div className="p-4">
                            {savedDocTitle ? (
                                <div className="hc-ai-file d-flex align-items-center p-3 rounded-4 mb-3">
                                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                        <span className="hc-ai-file-meta d-block mt-0 mb-1">Document Title</span>
                                        <span className="hc-ai-file-name fw-bold d-block">{savedDocTitle}</span>
                                    </div>
                                    <AiStatusPill tone="pending" label="Pending" />
                                </div>
                            ) : null}

                            <div className="hc-ai-callout d-flex align-items-start hc-ai-callout-pending mt-0">
                                <MdInfoOutline size={20} />
                                <div className="lh-base">
                                    <strong>The ingestion process has been initiated</strong> and your document is currently being fed into the RAG knowledge pipeline.
                                    <div className="mt-2">
                                        RAG indexing takes place in the background. We will notify you via email or notifications once indexing is complete, or you can check the document status on the Knowledge page where it will show as <strong>Active</strong> once ready.
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="px-4 pb-4 d-flex justify-content-end">
                            <button type="button" className="hc-ai-btn overflow-hidden d-inline-flex align-items-center justify-content-center gap-2 fw-bold text-nowrap hc-ai-btn-primary" onClick={handleModalClose}>
                                <span>Go to Knowledge Page</span>
                                <MdArrowForward size={16} />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}


